import React from 'react';
import clsx from 'clsx';

/**
 * StatusPill — full-radius status badge with required text label
 *
 * Rules:
 * - Always pill-shaped (border-radius: var(--radius-full))
 * - Text is UPPER-CASE, label font, tight tracking
 * - Tone map: green = active/positive, amber = pending/warning, red = danger ONLY
 * - NEVER render a "CONVERTED" pill (converted quotes leave the quote world)
 * - Red is reserved for true danger: overdue, expired, failed, rejected, cancelled
 * - All enums are humanized at the display boundary (no raw tokens in UI)
 *
 * Tone system (Iter-13 REQ-SYS-TONE-01):
 *   success  → green  (active, acknowledged, approved, completed, paid, won)
 *   warning  → amber  (pending, draft, in_progress, scheduled)
 *   danger   → red    (overdue, expired, failed, rejected, cancelled, lost)
 *   dark     → dark   (sent)
 *   neutral  → grey   (inactive, closed, archived, unknown)
 */

// ─── Tone definitions ───────────────────────────────────────────────────────
const TONES = {
  success: {
    bg: 'var(--color-success-bg)',
    fg: 'var(--color-success)',
    border: 'color-mix(in srgb, var(--color-success) 20%, transparent)',
  },
  warning: {
    bg: 'var(--color-warning-bg)',
    fg: 'var(--color-warning)',
    border: 'color-mix(in srgb, var(--color-warning) 20%, transparent)',
  },
  danger: {
    bg: 'var(--color-error-bg)',
    fg: 'var(--color-error)',
    border: 'color-mix(in srgb, var(--color-error) 20%, transparent)',
  },
  dark: {
    bg: 'var(--color-status-dark-bg)',
    fg: 'var(--color-status-dark-text)',
    border: 'transparent',
  },
  neutral: {
    bg: 'var(--color-status-neutral-bg)',
    fg: 'var(--color-status-neutral-text)',
    border: 'color-mix(in srgb, var(--color-status-neutral-text) 20%, transparent)',
  },
};

// ─── Status → Tone mapping (canonical; REQ-SYS-TONE-01) ─────────────────────
// Red = danger ONLY. Green = active/positive. Amber = pending/in-progress.
// "converted" is intentionally ABSENT — a converted quote becomes a Job (no pill).
const STATUS_TONE_MAP = {
  // Active / positive states → green
  active: 'success',
  acknowledged: 'success',
  approved: 'success',
  completed: 'success',
  paid: 'success',
  won: 'success',
  accepted: 'success',
  signed: 'success',
  scheduled: 'success',

  // Pending / in-progress → amber
  pending: 'warning',
  draft: 'warning',
  unsent: 'warning',
  in_progress: 'warning',
  partially_scheduled: 'warning',
  not_scheduled: 'warning',
  open: 'warning',
  awaiting: 'warning',
  review: 'warning',
  pending_signature: 'warning',
  // PT-21: WO-only pseudo-status used by <DetailPageHeader status="editing"> when
  // WorkOrderDetail is in inline edit mode (WO-2 State B). Not a real seed value —
  // never round-trips to backend; consumed only at the atom level for tone.
  editing: 'warning',

  // Danger (red = danger ONLY) → red
  overdue: 'danger',
  expired: 'danger',
  failed: 'danger',
  rejected: 'danger',
  declined: 'danger',
  cancelled: 'danger',
  lost: 'danger',
  past_due: 'danger',

  // Dark → sent
  sent: 'dark',

  // Neutral → inactive/closed
  inactive: 'neutral',
  closed: 'neutral',
  archived: 'neutral',
  on_hold: 'neutral',
};

// ─── Enum → Human Label mapping (REQ-SYS-VOCAB-01) ──────────────────────────
// Canonical humanization for all status/type/source enums.
// Applied at the display boundary — data layer keeps raw tokens.
const ENUM_LABELS = {
  // Statuses
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  // PT-23 (2026-07-26): invoice `void` state — register-only. No backend
  // producer yet (state machine tops out at Paid); do not build UI
  // affordances until backend `InvoiceStatusVoid` + transition ships.
  // `draft` intentionally NOT overridden here — change orders + quote drafts
  // consume `draft: 'Draft'` at line 109; invoice surfaces render `draft` as
  // `'Unsent'` via `getEnumLabel('invoice_status', ...)` (domain-scoped seed)
  // rather than the atom-level fallback.
  void: 'Void',
  expired: 'Expired',
  completed: 'Completed',
  in_progress: 'In Progress',
  partially_scheduled: 'Partially Scheduled',
  not_scheduled: 'Not Scheduled',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
  approved: 'Approved',
  rejected: 'Rejected',
  declined: 'Declined',
  lost: 'Lost',
  won: 'Accepted', // IDI-370: an accepted quote reads "Accepted" (green)
  open: 'Open',
  closed: 'Closed',
  archived: 'Archived',
  scheduled: 'Scheduled',
  accepted: 'Accepted',
  signed: 'Signed',
  // PT-21: WO-only pseudo-status — see STATUS_TONE_MAP note above.
  editing: 'Editing',

  // Signature statuses
  acknowledged: 'Signed',
  acknowledged_pending: 'Pending',
  acknowledged_pending_expired: 'Expired', // IDI-787: a link that ran out unsigned
  awaiting: 'Awaiting',
  pending_signature: 'Awaiting',
  pending_expired: 'Pending',

  // Property types
  residential: 'Residential',
  residential_new: 'Residential (New)',
  residential_existing: 'Residential (Existing)',
  commercial: 'Commercial',
  commercial_new: 'Commercial (New)',
  commercial_existing: 'Commercial (Existing)',
  industrial: 'Industrial',
  multi_family: 'Multi-Family',

  // Units
  sq_ft: 'Sq Ft',
  sqft: 'Sq Ft',
  square_feet: 'Square Feet',
  linear_ft: 'Linear Ft',
  linear_feet: 'Linear Feet',
  each: 'Each',
  per_unit: 'Per Unit',
  lump_sum: 'Lump Sum',
  hourly: 'Hourly',

  // Lead sources
  referral: 'Referral',
  website: 'Website',
  google: 'Google',
  social_media: 'Social Media',
  word_of_mouth: 'Word of Mouth',
  repeat_customer: 'Repeat Customer',
  home_show: 'Home Show',
  other: 'Other',

  // Job types
  new_construction: 'New Construction',
  remodel: 'Remodel',
  repair: 'Repair',
  service: 'Service',
  maintenance: 'Maintenance',
  inspection: 'Inspection',

  // Payment terms
  net_30: 'Net 30',
  net_15: 'Net 15',
  net_60: 'Net 60',
  due_on_receipt: 'Due on Receipt',
  cod: 'COD',

  // Misc
  past_due: 'Past Due',
  not_started: 'Not Started',
  review: 'Review',
  change_order: 'Change Order',
};

/**
 * Humanize any enum/status string for display.
 * Checks the canonical map first, then falls back to prettify.
 */
export function humanizeEnum(value) {
  if (!value) return '';
  const key = String(value).toLowerCase().trim();
  if (ENUM_LABELS[key]) return ENUM_LABELS[key];
  // Fallback: replace underscores/hyphens with spaces, title-case
  return prettify(value);
}

/**
 * Get the tone for a given status string.
 * Returns the tone key ('success', 'warning', 'danger', 'dark', 'neutral').
 */
export function getStatusTone(status) {
  if (!status) return 'neutral';
  const key = String(status).toLowerCase().trim().replace(/[\s-]+/g, '_');
  return STATUS_TONE_MAP[key] || 'neutral';
}

/**
 * StatusPill component
 *
 * @param {string} status - Raw status enum value
 * @param {string} [label] - Override display label (otherwise humanized from status)
 * @param {string} [tone] - Override tone ('success'|'warning'|'danger'|'dark'|'neutral')
 * @param {string} [size] - 'sm' | 'md' (default 'sm')
 * @param {string} [className] - Additional CSS classes
 */
export default function StatusPill({ status, label, tone, size = 'sm', className, children, ...rest }) {
  if (!status && !label && !children) return null;

  // Never render a CONVERTED pill
  const normalizedStatus = String(status || '').toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (normalizedStatus === 'converted') return null;

  const resolvedTone = tone || getStatusTone(status);
  const resolvedLabel = label || humanizeEnum(status);
  const colors = TONES[resolvedTone] || TONES.neutral;

  const sizeClasses = size === 'md'
    ? 'px-3 py-1 text-xs'
    : 'px-2 py-0.5 text-[11px]';

  return (
    <span
      className={clsx('inline-flex items-center rounded-full font-medium uppercase whitespace-nowrap', sizeClasses, className)}
      style={{
        backgroundColor: colors.bg,
        color: colors.fg,
        border: `1px solid ${colors.border}`,
        fontFamily: 'var(--font-body)',
        letterSpacing: '0.04em',
      }}
      {...rest}
    >
      {children || resolvedLabel}
    </span>
  );
}

// ─── Signature-specific pill (convenience wrapper) ───────────────────────────
// Signature tones: acknowledged = green, pending = amber, expired = red
export function SignaturePill({ signatureStatus, ...rest }) {
  if (!signatureStatus) return null;
  const key = String(signatureStatus).toLowerCase().trim().replace(/[\s-]+/g, '_');

  // Map signature-specific statuses to tones
  let tone = 'neutral';
  if (key === 'acknowledged' || key === 'signed' || key === 'accepted') {
    tone = 'success';
  } else if (key === 'pending' || key === 'pending_signature' || key === 'awaiting') {
    tone = 'warning';
  } else if (key === 'expired' || key === 'rejected' || key === 'acknowledged_pending_expired') {
    // IDI-787: acknowledged_pending_expired is a link that ran out unsigned
    // (cmd/expire) — it reads "Expired", red, never a green "Signed".
    tone = 'danger';
  }

  return <StatusPill status={signatureStatus} tone={tone} {...rest} />;
}

function prettify(s) {
  return String(s)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
